import { Injectable } from '@angular/core';
import { AppVersion } from '@ionic-native/app-version/ngx';
import { Device } from '@ionic-native/device/ngx';
import { NotificationData } from '@ionic-native/fcm/ngx';
import { FCM } from 'cordova-plugin-fcm-with-dependecy-updated/ionic/ngx';
import { Platform } from '@ionic/angular';
import { LocalNotifications } from '@ionic-native/local-notifications/ngx';
import { AlertService } from '../alert/alert.service';
import { ConnectService } from '../connect/connect.service';
import { StorageService } from '../storage/storage.service';
import * as Sentry from '@sentry/browser';
import { ProfileService } from '../profile/profile.service';
import { SentrySeverityLevel } from 'src/app/services/sentry/sentry.service';
import { lastValueFrom } from 'rxjs';
import { AuthService } from '../auth/auth.service';
import { AndroidPermissions } from '@ionic-native/android-permissions/ngx';

const TAG = 'PushNotificationService:';
const PUSH_TOKEN_KEY = 'push_token';

const POST_NOTIFICATIONS = 'android.permission.POST_NOTIFICATIONS';

@Injectable()
export class PushNotificationService {
  token: string;
  uuid: string | null = null;
  devicePlatform: string | null = null;
  version: string | null = null;
  manufacturer: string | null = null;
  model: string | null = null;
  inForeground = true;
  isOnline: boolean;
  hasPermission: boolean;
  // To avoid calling syncPushToken again
  // when notification permission requested, the platform.pause and .resume events triggers
  notificationPermissionInProcess = false;

  constructor(
    private fcm: FCM,
    private localNotifications: LocalNotifications,
    private appVersion: AppVersion,
    private device: Device,
    private platform: Platform,
    private alertSvc: AlertService,
    private connectSvc: ConnectService,
    private storageSvc: StorageService,
    private profileService: ProfileService,
    private authSvc: AuthService,
    private androidPermissions: AndroidPermissions
  ) {}

  async initializePush() {
    this.isOnline = window.navigator.onLine;
    if (this.platform.is('cordova')) {
      this.setPlatformListener();
      this.loadPhoneDetails();
      this.listenerPush();
      if (this.isOnline) {
        if (this.platform.is('ios')) {
          await this.fcm.deleteInstanceId();
        }
        this.syncPushToken();
      }
    } else {
      this.removePushDetails();
      Sentry.captureEvent({
        message: TAG + `(${this.profileService.profile.id}): ` + 'initializePush(): Not on Cordova. Token is removed.',
        level: SentrySeverityLevel.Debug,
      });
    }
  }

  async syncPushToken() {
    let onTokenRefresh;
    if (!(await this.authSvc.readToken())) {
      return;
    }
    if (this.hasPermission) {
      try {
        return new Promise((resolve, reject) => {
          const success = async (token: string) => {
            // istanbul ignore next
            if (!token) {
              Sentry.captureEvent({
                message: TAG + `(${this.profileService.profile.id}): ` + 'token is null',
                level: SentrySeverityLevel.Debug,
              });
              return;
            }
            this.token = token;
            await this.postPushDetails();
            onTokenRefresh.unsubscribe();
            resolve(token);
          };
          // istanbul ignore next
          this.fcm
            .getToken()
            .then(success)
            .catch(async () => {
              Sentry.captureEvent({
                message: TAG + `(${this.profileService.profile.id}): ` + 'getToken(): Failed to generate token.',
                level: SentrySeverityLevel.Debug,
              });
              reject();
            });
          onTokenRefresh = this.fcm.onTokenRefresh().subscribe(success);
        });
      } catch (e) {
        // istanbul ignore next
        Sentry.captureEvent({
          message: TAG + `(${this.profileService.profile.id}): ` + 'getToken Error: ' + e,
          level: SentrySeverityLevel.Debug,
          extra: e,
        });
      }
    } else {
      this.removePushDetails();
      Sentry.captureEvent({
        message: TAG + `(${this.profileService.profile.id}): ` + 'Notification permission denied!',
        level: SentrySeverityLevel.Debug,
      });
    }
  }

  async requestAndroidPermission() {
    if (parseInt(this.device.version, 10) <= 12) return true;

    const res = await this.androidPermissions.checkPermission(POST_NOTIFICATIONS);
    const isRejected = await this.storageSvc.get<boolean>('android_notification_permission_rejected');
    if (!res.hasPermission && !isRejected) {
      this.notificationPermissionInProcess = true;
      const res = await this.androidPermissions.requestPermission(POST_NOTIFICATIONS);
      if (res && !res.hasPermission) {
        this.storageSvc.set('android_notification_permission_rejected', true);
        return false;
      }
    }
    return true;
  }

  setPlatformListener() {
    this.platform.pause.subscribe(() => {
      // background
      this.inForeground = false;
    });

    this.platform.resume.subscribe(async () => {
      // foreground
      this.inForeground = true;

      if (this.notificationPermissionInProcess) {
        this.notificationPermissionInProcess = false;
        return;
      }
      await this.syncPushToken();
    });
  }

  loadPhoneDetails() {
    this.uuid = this.device.uuid;
    this.devicePlatform = this.device.platform;
    this.version = this.device.version;
    this.manufacturer = this.device.manufacturer;
    this.model = this.device.model;
  }

  postPushDetails() {
    this.appVersion.getVersionNumber().then(version => {
      this.connectSvc
        .create('/push_tokens', {
          push_token: {
            token: this.token,
            uuid: this.device.uuid,
            version: this.device.version,
            manufacturer: this.device.manufacturer,
            model: this.device.model,
            platform: this.device.platform,
            app_version: version,
          },
        })
        .subscribe();
    });
  }

  async removePushDetails() {
    try {
      await lastValueFrom(this.connectSvc.create('/accounts/remove_push_token', null));
      await this.storageSvc.remove(PUSH_TOKEN_KEY);
    } catch (e) {
      Sentry.captureEvent({
        message: TAG + `(${this.profileService.profile.id}): ` + 'removePushDetails(): Failed to remove push token.',
        level: SentrySeverityLevel.Debug,
      });
    }
  }

  async listenerPush() {
    if (this.platform.is('ios')) {
      this.fcm.requestPushPermission().then(async (permissionStatus: boolean) => {
        this.hasPermission = permissionStatus;
      });
    } else {
      this.hasPermission = await this.requestAndroidPermission();
    }
    this.fcm.onNotification().subscribe(data => {
      if (this.platform.is('ios')) {
        const alert = data.aps.alert;
        /* istanbul ignore next */
        const title = alert.message_title || alert.title;
        /* istanbul ignore next */
        const body = alert.message_body || alert.body;
        this.onNotification(data, title, body);
      } else {
        /* istanbul ignore next */
        const title = data.message_title || data.title;
        /* istanbul ignore next */
        const body = data.message_body || data.body;
        this.onNotification(data, title, body);
      }
    });
  }

  onNotification(data: NotificationData, title: string, body: string) {
    /* istanbul ignore next */
    if (!data.wasTapped) {
      /* istanbul ignore next */
      if (!this.inForeground) this.notificationWasNotTapped(title, body);
      /* istanbul ignore next */
      if (this.inForeground) this.localNotification(title, body);
    }
  }

  localNotification(title, body) {
    this.localNotifications.schedule({
      title,
      text: body,
      foreground: true,
      icon: 'assets://icon',
    });
  }

  notificationWasTapped(title: string, body: string) {
    this.alertSvc.presentAlert(title, '', body, 'Dismiss');
  }

  notificationWasNotTapped(title: string, body: string) {
    this.alertSvc.presentAlert(title, '', body, `Dismiss`);
  }
}
